import React, { createContext, useContext, useState, useEffect } from 'react';
import { web3Service } from '../services/web3Service';
import { useAuth } from './AuthContext';
import { authService } from '../services/authService';

const Web3Context = createContext({});

export const useWeb3 = () => {
  const context = useContext(Web3Context);
  if (!context) {
    throw new Error('useWeb3 must be used within Web3Provider');
  }
  return context;
};

export const Web3Provider = ({ children }) => {
  const { user } = useAuth();
  const [walletAddress, setWalletAddress] = useState(null);
  const [chainId, setChainId] = useState(null);
  const [provider, setProvider] = useState(null);
  const [balance, setBalance] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Setup listeners for account and network changes
    web3Service?.onAccountsChanged((accounts) => {
      if (accounts?.length === 0) {
        handleDisconnect();
      } else {
        setWalletAddress(accounts?.[0]);
        updateBalance(accounts?.[0]);
      }
    });

    web3Service?.onChainChanged((chainId) => {
      setChainId(parseInt(chainId, 16));
      window?.location?.reload();
    });

    return () => {
      web3Service?.removeListeners();
    };
  }, []);

  const updateBalance = async (address) => {
    const { balance: ethBalance, error } = await web3Service?.getBalance(address);
    if (!error) {
      setBalance(ethBalance);
    }
  };

  const connectWallet = async () => {
    setIsConnecting(true);
    setError(null);

    try {
      const { address, chainId: networkId, provider: providerName, error } = 
        await web3Service?.connectMetaMask();

      if (error) {
        setError(error?.message);
        setIsConnecting(false);
        return { success: false, error };
      }

      setWalletAddress(address);
      setChainId(networkId);
      setProvider(providerName);

      // Update balance
      await updateBalance(address);

      // Save to database if user is logged in
      if (user) {
        await authService?.connectWallet(address);
      }

      setIsConnecting(false);
      return { success: true, address, chainId: networkId, provider: providerName };
    } catch (err) {
      const errorMessage = err?.message || 'Failed to connect wallet';
      setError(errorMessage);
      setIsConnecting(false);
      return { success: false, error: { message: errorMessage } };
    }
  };

  const disconnectWallet = async () => {
    try {
      await web3Service?.disconnect();
      await handleDisconnect();

      // Update database if user is logged in
      if (user) {
        await authService?.disconnectWallet();
      }

      return { success: true };
    } catch (err) {
      return { success: false, error: { message: err?.message } };
    }
  };

  const handleDisconnect = () => {
    setWalletAddress(null);
    setChainId(null);
    setProvider(null);
    setBalance(null);
    setError(null);
  };

  const switchNetwork = async (targetChainId) => {
    const result = await web3Service?.switchNetwork(targetChainId);
    if (result?.success) {
      setChainId(targetChainId);
    }
    return result;
  };

  const getTransactions = async (limit = 10) => {
    if (!walletAddress) {
      return { transactions: [], error: { message: 'No wallet connected' } };
    }
    return await web3Service?.getTransactionHistory(walletAddress, limit);
  };

  const value = {
    walletAddress,
    chainId,
    provider,
    balance,
    isConnecting,
    error,
    isConnected: !!walletAddress,
    connectWallet,
    disconnectWallet,
    switchNetwork,
    getTransactions,
    updateBalance: () => walletAddress && updateBalance(walletAddress)
  };

  return (
    <Web3Context.Provider value={value}>
      {children}
    </Web3Context.Provider>
  );
};

export default Web3Provider;